import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useTelegram } from '@/hooks/useTelegram';
import { ARCHETYPE_INFO } from '@/types';
import type { Archetype } from '@/types';
import {
  getAllGuilds,
  getMyGuild,
  getGuildMembers,
  joinGuild,
  leaveGuild,
  getGlobalLeaderboard,
  getGuildLeaderboard,
  type Guild,
  type GuildMember,
  type LeaderboardEntry,
  type GuildLeaderboardEntry,
} from '@/services/social';

interface GuildsPageProps {
  onClose: () => void;
  currentUserId?: string;
}

type Tab = 'my-guild' | 'all-guilds' | 'players' | 'guild-ranking';

export function GuildsPage({ onClose, currentUserId }: GuildsPageProps) {
  const [activeTab, setActiveTab] = useState<Tab>('my-guild');
  const [myGuild, setMyGuild] = useState<Guild | null>(null);
  const [allGuilds, setAllGuilds] = useState<Guild[]>([]);
  const [guildMembers, setGuildMembers] = useState<GuildMember[]>([]);
  const [playerLeaderboard, setPlayerLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [guildLeaderboard, setGuildLeaderboard] = useState<GuildLeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedGuild, setSelectedGuild] = useState<Guild | null>(null);
  
  const { hapticFeedback } = useTelegram();
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      setIsLoading(true);
      
      const [myGuildRes, allGuildsRes, playersRes, guildRankingRes] = await Promise.all([
        getMyGuild(),
        getAllGuilds(),
        getGlobalLeaderboard(50),
        getGuildLeaderboard(),
      ]);
      
      setMyGuild(myGuildRes.guild);
      setAllGuilds(allGuildsRes.guilds);
      setPlayerLeaderboard(playersRes.leaderboard);
      setGuildLeaderboard(guildRankingRes.leaderboard);
      
      // Загружаем участников своей гильдии
      if (myGuildRes.guild) {
        const membersRes = await getGuildMembers(myGuildRes.guild.id);
        setGuildMembers(membersRes.members);
      }
    } catch (error) {
      console.error('Failed to load social data:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleJoinGuild = async (guildId: string) => {
    try {
      setActionLoading(guildId);
      await joinGuild(guildId);
      hapticFeedback('success');
      await loadData();
      setSelectedGuild(null);
    } catch (error: any) {
      console.error('Failed to join guild:', error);
      hapticFeedback('error');
      alert(error.message);
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleLeaveGuild = async () => {
    if (!myGuild) return;
    
    if (!confirm('Точно хочешь покинуть гильдию?')) return;
    
    try {
      setActionLoading(myGuild.id);
      await leaveGuild(myGuild.id);
      hapticFeedback('light');
      await loadData();
    } catch (error: any) {
      console.error('Failed to leave guild:', error);
      hapticFeedback('error');
      alert(error.message);
    } finally {
      setActionLoading(null);
    }
  };
  
  const tabs: { id: Tab; label: string; emoji: string }[] = [
    { id: 'my-guild', label: 'Моя', emoji: '🏠' },
    { id: 'all-guilds', label: 'Все', emoji: '🏰' },
    { id: 'players', label: 'Игроки', emoji: '👤' },
    { id: 'guild-ranking', label: 'Топ', emoji: '🏆' },
  ];
  
  return (
    <div className="min-h-screen bg-primary-950 flex flex-col safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-primary-800/50">
        <button onClick={onClose} className="text-primary-400">
          ← Назад
        </button>
        <h1 className="text-lg font-semibold text-white">Сообщество</h1>
        <div className="w-16" />
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-primary-800/50">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-3 flex flex-col items-center gap-1 transition-colors ${
              activeTab === tab.id
                ? 'bg-primary-800/50 text-white'
                : 'text-primary-500'
            }`}
          >
            <span className="text-lg">{tab.emoji}</span>
            <span className="text-xs">{tab.label}</span>
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
          </div>
        ) : (
          <>
            {activeTab === 'my-guild' && (
              <MyGuildView
                guild={myGuild}
                members={guildMembers}
                onLeave={handleLeaveGuild}
                isLeaving={actionLoading === myGuild?.id}
                onFindGuild={() => setActiveTab('all-guilds')}
                allGuilds={allGuilds}
              />
            )}
            
            {activeTab === 'all-guilds' && (
              <AllGuildsView
                guilds={allGuilds}
                onSelect={setSelectedGuild}
                currentGuildId={myGuild?.id}
              />
            )}
            
            {activeTab === 'players' && (
              <PlayerLeaderboardView
                leaderboard={playerLeaderboard}
                currentUserId={currentUserId}
              />
            )}
            
            {activeTab === 'guild-ranking' && (
              <GuildLeaderboardView
                leaderboard={guildLeaderboard}
                myGuildId={myGuild?.id}
              />
            )}
          </>
        )}
      </div>
      
      {/* Guild Detail Modal */}
      <AnimatePresence>
        {selectedGuild && (
          <GuildDetailModal
            guild={selectedGuild}
            onClose={() => setSelectedGuild(null)}
            onJoin={() => handleJoinGuild(selectedGuild.id)}
            isJoining={actionLoading === selectedGuild.id}
            canJoin={!myGuild}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// MY GUILD VIEW
// ============================================

interface MyGuildViewProps {
  guild: Guild | null;
  members: GuildMember[];
  onLeave: () => void;
  isLeaving: boolean;
  onFindGuild: () => void;
  allGuilds: Guild[];
}

function MyGuildView({ guild, members, onLeave, isLeaving, onFindGuild, allGuilds }: MyGuildViewProps) {
  // Special case: show Первопроходцы guild for all users as default
  if (!guild) {
    const defaultGuild = allGuilds.find(g => g.name.toLowerCase().includes('первопроходц'));
    const chatLink = defaultGuild?.telegramChatLink;

    return (
      <div className="space-y-6">
        {/* Первопроходцы default guild card */}
        <div className="bg-gradient-to-br from-amber-900/30 to-orange-900/20 rounded-xl p-4 border border-amber-800/30">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-xl bg-amber-800/30 flex items-center justify-center">
              <span className="text-3xl">{defaultGuild?.emoji || '🏕️'}</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Первопроходцы</h2>
              <p className="text-sm text-amber-300/70">
                Те, кто начал путь первыми
              </p>
            </div>
          </div>

          <p className="text-primary-300 text-sm mb-4">
            Ты — один из первых странников в городе. Все, кто начинают сейчас, объединены в этой гильдии.
          </p>

          {/* Telegram chat link */}
          {chatLink && (
            <a
              href={chatLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#0088cc] hover:bg-[#0099dd] text-white font-medium transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
              </svg>
              Чат Первопроходцев
            </a>
          )}
        </div>

        {/* Option to find other guilds */}
        <div className="text-center">
          <p className="text-primary-500 text-sm mb-3">
            Или присоединись к другой гильдии:
          </p>
          <Button onClick={onFindGuild} variant="secondary">
            Найти гильдию
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Guild Header */}
      <div className="bg-primary-800/50 rounded-xl p-4">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-xl bg-primary-700/50 flex items-center justify-center">
            <span className="text-3xl">{guild.emoji || '🏰'}</span>
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">{guild.name}</h2>
            <p className="text-sm text-primary-400">
              {guild.memberCount} участников • {guild.weeklyXp} XP за неделю
            </p>
          </div>
        </div>
        
        {guild.description && (
          <p className="text-primary-300 text-sm">{guild.description}</p>
        )}

        {/* Telegram chat link */}
        {guild.telegramChatLink && (
          <a
            href={guild.telegramChatLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3 mt-4 rounded-xl bg-[#0088cc] hover:bg-[#0099dd] text-white font-medium transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
            </svg>
            Чат гильдии
          </a>
        )}

        <div className="mt-4 pt-4 border-t border-primary-700/50 flex items-center justify-between">
          <span className="text-sm text-primary-400">
            Твоя роль: <span className="text-primary-200">{getRoleName(guild.userRole || 'member')}</span>
          </span>
          
          {guild.userRole !== 'leader' && (
            <button
              onClick={onLeave}
              disabled={isLeaving}
              className="text-sm text-red-400 hover:text-red-300"
            >
              {isLeaving ? 'Выходим...' : 'Покинуть'}
            </button>
          )}
        </div>
      </div>
      
      {/* Members */}
      <div>
        <h3 className="text-sm font-medium text-primary-400 mb-3">
          Участники ({members.length})
        </h3>
        <div className="space-y-2">
          {members.map((member, index) => (
            <MemberRow key={member.id} member={member} rank={index + 1} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================
// ALL GUILDS VIEW
// ============================================

interface AllGuildsViewProps {
  guilds: Guild[];
  onSelect: (guild: Guild) => void;
  currentGuildId?: string;
}

function AllGuildsView({ guilds, onSelect, currentGuildId }: AllGuildsViewProps) {
  return (
    <div className="space-y-3">
      {guilds.map(guild => (
        <motion.button
          key={guild.id}
          whileTap={{ scale: 0.98 }}
          onClick={() => onSelect(guild)}
          className={`w-full p-4 rounded-xl text-left transition-all ${
            guild.id === currentGuildId
              ? 'bg-primary-700/70 border-2 border-primary-500'
              : 'bg-primary-800/50 hover:bg-primary-800/70'
          }`}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary-700/50 flex items-center justify-center flex-shrink-0">
              <span className="text-2xl">{guild.emoji || '🏰'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-white truncate">{guild.name}</h3>
                {guild.id === currentGuildId && (
                  <span className="text-xs bg-primary-500 text-white px-2 py-0.5 rounded">
                    Твоя
                  </span>
                )}
              </div>
              <p className="text-sm text-primary-400">
                {guild.memberCount} участников
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-sm font-medium text-primary-300">{guild.weeklyXp}</p>
              <p className="text-xs text-primary-500">XP/нед</p>
            </div>
          </div>
        </motion.button>
      ))}
    </div>
  );
}

// ============================================
// PLAYER LEADERBOARD VIEW
// ============================================

interface PlayerLeaderboardViewProps {
  leaderboard: LeaderboardEntry[];
  currentUserId?: string;
}

function PlayerLeaderboardView({ leaderboard, currentUserId }: PlayerLeaderboardViewProps) {
  return (
    <div className="space-y-2">
      {leaderboard.map(entry => {
        const isMe = entry.oderId === currentUserId;
        const archetypeInfo = entry.archetype 
          ? ARCHETYPE_INFO[entry.archetype as Archetype]
          : null;
        
        return (
          <div
            key={entry.oderId}
            className={`p-3 rounded-xl flex items-center gap-3 ${
              isMe ? 'bg-primary-700/70 border border-primary-500' : 'bg-primary-800/50'
            }`}
          >
            {/* Rank */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              entry.rank === 1 ? 'bg-amber-500 text-white' :
              entry.rank === 2 ? 'bg-gray-400 text-white' :
              entry.rank === 3 ? 'bg-amber-700 text-white' :
              'bg-primary-700 text-primary-300'
            }`}>
              {entry.rank}
            </div>
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-white truncate">
                  {entry.characterName || entry.userName}
                </span>
                {archetypeInfo && (
                  <span className="text-sm">{archetypeInfo.emoji}</span>
                )}
                {isMe && (
                  <span className="text-xs text-primary-400">(ты)</span>
                )}
              </div>
              <p className="text-xs text-primary-500">
                Ур. {entry.characterLevel} {entry.guildName && `• ${entry.guildName}`}
              </p>
            </div>
            
            {/* XP */}
            <div className="text-right">
              <p className="text-sm font-medium text-primary-200">{entry.totalXp}</p>
              <p className="text-xs text-primary-500">+{entry.weeklyXp}/нед</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// GUILD LEADERBOARD VIEW
// ============================================

interface GuildLeaderboardViewProps {
  leaderboard: GuildLeaderboardEntry[];
  myGuildId?: string;
}

function GuildLeaderboardView({ leaderboard, myGuildId }: GuildLeaderboardViewProps) {
  return (
    <div className="space-y-2">
      {leaderboard.map(entry => {
        const isMyGuild = entry.guildId === myGuildId;
        
        return (
          <div
            key={entry.guildId}
            className={`p-3 rounded-xl flex items-center gap-3 ${
              isMyGuild ? 'bg-primary-700/70 border border-primary-500' : 'bg-primary-800/50'
            }`}
          >
            {/* Rank */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              entry.rank === 1 ? 'bg-amber-500 text-white' :
              entry.rank === 2 ? 'bg-gray-400 text-white' :
              entry.rank === 3 ? 'bg-amber-700 text-white' :
              'bg-primary-700 text-primary-300'
            }`}>
              {entry.rank}
            </div>
            
            {/* Info */}
            <div className="w-10 h-10 rounded-lg bg-primary-700/50 flex items-center justify-center">
              <span className="text-xl">{entry.emoji || '🏰'}</span>
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-white truncate">{entry.guildName}</span>
                {isMyGuild && (
                  <span className="text-xs text-primary-400">(твоя)</span>
                )}
              </div>
              <p className="text-xs text-primary-500">
                {entry.memberCount} участников
              </p>
            </div>
            
            {/* XP */}
            <div className="text-right">
              <p className="text-sm font-medium text-primary-200">{entry.weeklyXp}</p>
              <p className="text-xs text-primary-500">XP/нед</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// GUILD DETAIL MODAL
// ============================================

interface GuildDetailModalProps {
  guild: Guild;
  onClose: () => void;
  onJoin: () => void;
  isJoining: boolean;
  canJoin: boolean;
}

function GuildDetailModal({ guild, onClose, onJoin, isJoining, canJoin }: GuildDetailModalProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-primary-900 rounded-t-3xl p-6 w-full max-w-lg safe-area-bottom"
      >
        <div className="w-12 h-1 bg-primary-700 rounded-full mx-auto mb-6" />
        
        <div className="text-center mb-6">
          <div className="w-20 h-20 rounded-xl bg-primary-800/50 flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">{guild.emoji || '🏰'}</span>
          </div>
          <h2 className="text-2xl font-bold text-white">{guild.name}</h2>
          <p className="text-primary-400">
            {guild.memberCount} участников • {guild.weeklyXp} XP за неделю
          </p>
        </div>
        
        {guild.description && (
          <p className="text-primary-300 text-center mb-6">{guild.description}</p>
        )}
        
        <div className="space-y-3">
          {canJoin && !guild.isMember ? (
            <Button onClick={onJoin} fullWidth loading={isJoining}>
              Вступить в гильдию
            </Button>
          ) : guild.isMember ? (
            <div className="text-center py-3 text-primary-400">
              Ты уже в этой гильдии
            </div>
          ) : (
            <div className="text-center py-3 text-primary-500 text-sm">
              Сначала покинь текущую гильдию
            </div>
          )}
          
          <Button onClick={onClose} variant="ghost" fullWidth>
            Закрыть
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// HELPERS
// ============================================

function MemberRow({ member, rank }: { member: GuildMember; rank: number }) {
  const archetypeInfo = member.archetype 
    ? ARCHETYPE_INFO[member.archetype as Archetype]
    : null;
  
  return (
    <div className="p-3 rounded-xl bg-primary-800/30 flex items-center gap-3">
      <div className="w-6 text-center text-sm text-primary-500">{rank}</div>
      
      <div className="w-8 h-8 rounded-full bg-primary-700/50 flex items-center justify-center">
        {archetypeInfo ? (
          <span className="text-sm">{archetypeInfo.emoji}</span>
        ) : (
          <span className="text-sm">👤</span>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white truncate">
            {member.characterName || member.userName}
          </span>
          {member.role !== 'member' && (
            <span className={`text-xs px-1.5 py-0.5 rounded ${
              member.role === 'leader' ? 'bg-amber-500/20 text-amber-400' : 'bg-primary-500/20 text-primary-300'
            }`}>
              {getRoleName(member.role)}
            </span>
          )}
        </div>
        <p className="text-xs text-primary-500">Ур. {member.characterLevel}</p>
      </div>
      
      <div className="text-right">
        <p className="text-sm text-primary-300">{member.weeklyXp}</p>
        <p className="text-xs text-primary-500">XP</p>
      </div>
    </div>
  );
}

function getRoleName(role: string): string {
  const names: Record<string, string> = {
    leader: 'Лидер',
    officer: 'Офицер',
    member: 'Участник',
  };
  return names[role] || role;
}
